"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { useCustomerPanelTranslations } from "@/lib/customer-panel-utils";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import CustomerApiService, {
  type ReservationListItem,
  type ReservationDetail,
  type ReservationsResponse,
} from "@/services/CustomerApiService";
import { UrlBuilder } from "@/lib/url-builder";
import type { PageUrlMap } from "@/types/blog";
import SplitPayButton from "@/components/customer/SplitPayButton";

interface CustomerReservationsProps {
  language?: string;
  languageId?: number;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  __grapesPreview?: boolean;
}

const RESERVATION_STATUS_KEYS = ['pending', 'confirmed', 'cancelled', 'completed'] as const;

// Ödeme durumu anahtarları – metinler t() ile dil kayıtlarından alınır
const PAYMENT_STATUS_KEYS: Record<string, string> = {
  unpaid: 'customer_panel.reservations.payment_unpaid',
  partial: 'customer_panel.reservations.payment_partial',
  paid: 'customer_panel.reservations.payment_paid',
};

function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    const loc = locale === 'tr' ? 'tr-TR' : 'en-GB';
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(loc, { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

function isSplitPayable(status: string | undefined): boolean {
  if (!status) return true;
  const s = status.toLowerCase();
  return s !== 'completed' && s !== 'paid' && s !== 'cancelled' && s !== 'refunded';
}

function formatAmount(amount: string | number | undefined, symbol?: string, locale = 'en'): string {
  if (amount === undefined || amount === null) return '—';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(n)) return '—';
  const loc = locale === 'tr' ? 'tr-TR' : 'en-GB';
  const s = n.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return symbol ? `${s} ${symbol}` : s;
}

export default function CustomerReservations({
  language = 'en',
  languageId,
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
  __grapesPreview = false,
  ...restProps
}: CustomerReservationsProps) {
  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
  const backendAttributes = getBackendRenderAttributes(
    'page_layouts/CustomerReservations',
    { language, translations, pageUrls },
    isBackendRender
  );

  const { getVariable } = useLanguageVariables();
  const t = useCustomerPanelTranslations(translations);
  const { isAuthenticated, customer, isLoading: authLoading } = useCustomerAuth();

  const [reservations, setReservations] = useState<ReservationListItem[]>([]);
  const [meta, setMeta] = useState<ReservationsResponse['meta']>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ReservationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [viewingVoucherId, setViewingVoucherId] = useState<number | null>(null);
  const [openVoucherSection, setOpenVoucherSection] = useState(false);
  const [splitPayError, setSplitPayError] = useState<string | null>(null);

  const reservationStatusLabel = useCallback((status: string) => {
    if (RESERVATION_STATUS_KEYS.includes(status as typeof RESERVATION_STATUS_KEYS[number])) {
      return t(`customer_panel.reservations.${status}`);
    }
    return status;
  }, [t]);

  // Tour template key ile rezervasyon sayfası URL'i (tour-cat veya tour)
  const makeReservationUrl = useMemo(() => {
    if (!pageUrls || Object.keys(pageUrls).length === 0) return '#';
    try {
      const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
      const url = builder.buildUrl('private-tours', {}) || builder.buildUrl('group-tours', {});
      return url && url !== '#' ? url : '#';
    } catch {
      return '#';
    }
  }, [pageUrls, language, defaultLanguage]);

  useEffect(() => {
    if (isBackendRender || !isAuthenticated || !customer) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    CustomerApiService.getReservations({ page, per_page: 10 })
      .then((res) => {
        if (!cancelled) {
          setReservations(res.data);
          setMeta(res.meta);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = translations['customer_panel.reservations.load_error']
            || getVariable('customer_panel.reservations.load_error', '');
          setError(err instanceof Error ? err.message : msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isBackendRender, isAuthenticated, customer, page, translations, getVariable]);

  useEffect(() => {
    if (detailId == null) {
      setDetail(null);
      setDetailError(null);
      setViewingVoucherId(null);
      setOpenVoucherSection(false);
      return;
    }
    setViewingVoucherId(null);
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    CustomerApiService.getReservationDetail(detailId)
      .then((res) => {
        if (!cancelled) {
          setDetail(res);
          if (openVoucherSection && res.vouchers?.length) {
            setViewingVoucherId(res.vouchers[0].id);
            setOpenVoucherSection(false);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = translations['customer_panel.reservations.detail_load_error']
            || getVariable('customer_panel.reservations.detail_load_error', '');
          setDetailError(err instanceof Error ? err.message : msg);
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [detailId, openVoucherSection, translations, getVariable]);

  if (!isBackendRender && (!isAuthenticated || !customer) && !authLoading) {
    return null;
  }

  const pagination = meta?.pagination;
  const total = pagination?.total ?? 0;
  const pageCount = pagination?.pageCount ?? 1;
  const currentPage = pagination?.page ?? 1;
  const vouchers = detail?.vouchers ?? [];
  const viewingVoucher = detail?.vouchers?.find((v) => v.id === viewingVoucherId);
  const voucherFileUrl = viewingVoucher && detail ? CustomerApiService.getVoucherFileUrl(detail.id, viewingVoucher.id) : null;

  return (
    <div {...backendAttributes}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('customer_panel.reservations.title', 'My Reservations')}
        </h1>
      </div>

      {isBackendRender && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-600">{t('customer_panel.reservations.empty', 'You don\'t have any reservations yet.')}</p>
        </div>
      )}

      {!isBackendRender && (
        <>
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" aria-hidden="true" />
              <span className="sr-only">{t('customer_panel.common.loading', 'Loading...')}</span>
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && reservations.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="mb-6">
                  <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {t('customer_panel.reservations.empty', 'You don\'t have any reservations yet.')}
                </h2>
                <Link
                  href={makeReservationUrl}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {t('customer_panel.reservations.make_reservation', 'Make Reservation')}
                </Link>
              </div>
            </div>
          )}

          {!loading && !error && reservations.length > 0 && (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.reservations.reservation_no', 'Reservation No')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.reservations.product', 'Product')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.reservations.status', 'Status')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.reservations.payment_status', 'Payment')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.reservations.date', 'Date')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.reservations.total', 'Total')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.reservations.actions', 'Actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {reservations.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.reservation_no ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.product?.name ?? '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                            r.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            r.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {reservationStatusLabel(r.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex flex-col gap-1">
                            <span>
                              {r.payment_status && PAYMENT_STATUS_KEYS[r.payment_status]
                                ? t(PAYMENT_STATUS_KEYS[r.payment_status], r.payment_status)
                                : (r.payment_status ?? '—')}
                            </span>
                            {(r.open_dues_count ?? 0) > 0 && (
                              <span className="inline-flex w-fit rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                                {t('customer_panel.reservations.open_dues_badge', '{count} open due(s)').replace('{count}', String(r.open_dues_count))}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(r.travel_date ?? r.reservation_date ?? r.created_at, language)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">
                          {formatAmount(r.total_amount, r.currency?.symbol, language)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setDetailId(r.id)}
                              className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                            >
                              {t('customer_panel.reservations.view_detail', 'View detail')}
                            </button>
                            {r.status === 'confirmed' && r.payment_status === 'paid' && (
                              <button
                                type="button"
                                onClick={() => { setOpenVoucherSection(true); setDetailId(r.id); }}
                                className="text-green-600 hover:text-green-800 font-medium text-sm"
                              >
                                {t('customer_panel.reservations.voucher', 'Voucher')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pageCount > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3">
                  <span className="text-sm text-gray-700">
                    {t('customer_panel.common.page', 'Page')} {currentPage} / {pageCount} ({total} {t('customer_panel.reservations.total_count', 'total')})
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="rounded border border-gray-300 bg-white px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {t('customer_panel.common.previous', 'Previous')}
                    </button>
                    <button
                      type="button"
                      disabled={currentPage >= pageCount}
                      onClick={() => setPage((p) => p + 1)}
                      className="rounded border border-gray-300 bg-white px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {t('customer_panel.common.next', 'Next')}
                    </button>
                  </div>
                </div>
              )}

              <div className="pt-4">
                <Link
                  href={makeReservationUrl}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  {t('customer_panel.reservations.make_reservation', 'Make Reservation')}
                </Link>
              </div>
            </div>
          )}

          {/* Rezervasyon detay modal */}
          {detailId != null && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="reservation-detail-title"
              onClick={() => setDetailId(null)}
            >
              <div
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 id="reservation-detail-title" className="text-xl font-semibold text-gray-900">
                    {t('customer_panel.reservations.detail_title', 'Reservation detail')}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setDetailId(null)}
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label={t('customer_panel.common.close', 'Close')}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="px-6 py-4 overflow-y-auto flex-1">
                  {detailLoading && (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
                    </div>
                  )}
                  {detailError && (
                    <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{detailError}</div>
                  )}
                  {!detailLoading && !detailError && detail && (
                    <div className="space-y-6">
                      {/* Bölüm 5: Rezervasyon durumu mesajları */}
                      <div className={`rounded-lg border p-4 ${detail.status === 'confirmed' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                        <p className="text-sm font-medium">
                          {detail.status === 'confirmed'
                            ? getVariable('tour_detail.reservation_confirmed', '')
                            : getVariable('tour_detail.reservation_request_received', '')}
                        </p>
                      </div>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <dt className="text-gray-500">{t('customer_panel.reservations.reservation_no', 'Reservation No')}</dt>
                          <dd className="font-medium text-gray-900">{detail.reservation_no ?? '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">{t('customer_panel.reservations.product', 'Product')}</dt>
                          <dd className="font-medium text-gray-900">{detail.product?.name ?? '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">{t('customer_panel.reservations.status', 'Status')}</dt>
                          <dd>
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              detail.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              detail.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-amber-100 text-amber-800'
                            }`}>
                              {reservationStatusLabel(detail.status)}
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">{t('customer_panel.reservations.payment_status', 'Payment')}</dt>
                          <dd className="text-gray-900">{detail.payment_status && PAYMENT_STATUS_KEYS[detail.payment_status] ? t(PAYMENT_STATUS_KEYS[detail.payment_status], detail.payment_status) : (detail.payment_status ?? '—')}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">{t('customer_panel.reservations.date', 'Date')}</dt>
                          <dd className="text-gray-900">{formatDate(detail.travel_date ?? detail.reservation_date ?? detail.created_at, language)}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">{t('customer_panel.reservations.total', 'Total')}</dt>
                          <dd className="font-medium text-gray-900">{formatAmount(detail.total_amount, detail.currency?.symbol, language)}</dd>
                        </div>
                      </dl>

                      {/* Tutarlar & Ödeme özeti */}
                      <div className="border-t border-gray-200 pt-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">
                          {t('customer_panel.reservations.amounts_title', 'Amounts & payment')}
                        </h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <div>
                            <dt className="text-gray-500">{t('customer_panel.reservations.total', 'Total')}</dt>
                            <dd className="font-medium text-gray-900">{formatAmount(detail.total_amount, detail.currency?.symbol, language)}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">{t('customer_panel.reservations.paid_amount', 'Paid')}</dt>
                            <dd className="font-medium text-green-700">{formatAmount(detail.paid_amount, detail.currency?.symbol, language)}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">{t('customer_panel.reservations.remaining_amount', 'Remaining')}</dt>
                            <dd className="font-medium text-gray-900">
                              {formatAmount(
                                Math.max(0, parseFloat(String(detail.total_amount ?? 0)) - parseFloat(String(detail.paid_amount ?? 0))),
                                detail.currency?.symbol,
                                language
                              )}
                            </dd>
                          </div>
                        </dl>
                      </div>

                      {detail.payment_schedule && detail.payment_schedule.length > 0 && (
                        <div className="border-t border-gray-200 pt-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-3">
                            {t('customer_panel.reservations.payment_schedule', 'Payment schedule')}
                          </h3>
                          {splitPayError && (
                            <p className="mb-2 text-xs text-red-600" role="alert">{splitPayError}</p>
                          )}
                          <ul className="space-y-2">
                            {detail.payment_schedule.map((row) => {
                              const paid = row.status === 'completed' || row.status === 'paid';
                              return (
                              <li key={row.id} className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2">
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {formatDate(row.due_date, language)}
                                  </span>
                                  <span className="text-gray-600 ml-2">
                                    {formatAmount(row.total_due ?? row.amount, detail.currency?.symbol, language)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`rounded px-2 py-0.5 text-xs ${paid ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                                    {paid
                                      ? t('customer_panel.reservations.payment_paid', 'Paid')
                                      : t('customer_panel.reservations.payment_pending', 'Pending')}
                                  </span>
                                  {detail.id && isSplitPayable(row.status) && (
                                    <SplitPayButton
                                      reservationId={detail.id}
                                      splitId={row.id}
                                      paymentUrl={row.payment_url}
                                      label={t('customer_panel.reservations.pay_link', 'Pay')}
                                      loadingLabel={t('customer_panel.common.loading', 'Loading...')}
                                      onError={(msg) => setSplitPayError(msg)}
                                    />
                                  )}
                                </div>
                              </li>
                            );})}
                          </ul>
                        </div>
                      )}

                      {/* Ödemeler listesi */}
                      {detail.payments && detail.payments.length > 0 && (
                        <div className="border-t border-gray-200 pt-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-3">
                            {t('customer_panel.reservations.payments_list', 'Payments')}
                          </h3>
                          <ul className="space-y-2">
                            {detail.payments.map((pay) => (
                              <li key={pay.id} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
                                <span className="font-medium text-gray-900">{pay.payment_no ?? `#${pay.id}`}</span>
                                <span className="text-gray-700">{formatAmount(pay.amount, (pay as { currency?: { symbol?: string } }).currency?.symbol ?? detail.currency?.symbol, language)}</span>
                                <span className={`rounded px-2 py-0.5 text-xs ${pay.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                                  {pay.status === 'completed' ? t('customer_panel.reservations.payment_paid', 'Paid') : pay.status ?? '—'}
                                </span>
                                {pay.paid_at && <span className="text-gray-500 text-xs">{formatDate(pay.paid_at, language)}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Odalar */}
                      {detail.rooms && Array.isArray(detail.rooms) && detail.rooms.length > 0 && (
                        <div className="border-t border-gray-200 pt-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-3">
                            {t('customer_panel.reservations.rooms_title', 'Rooms')}
                          </h3>
                          <ul className="space-y-3">
                            {detail.rooms.map((room, idx) => (
                              <li key={idx} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
                                <div className="font-medium text-gray-900 mb-1">
                                  {t('customer_panel.reservations.room', 'Room')} {idx + 1}
                                  {(room.adults != null || room.children != null) && (
                                    <span className="text-gray-500 font-normal ml-2">
                                      ({[
                                        room.adults != null && room.adults > 0 && `${room.adults} ${t('customer_panel.reservations.adults')}`,
                                        room.children != null && room.children > 0 && `${room.children} ${t('customer_panel.reservations.children')}`,
                                      ].filter(Boolean).join(', ')})
                                    </span>
                                  )}
                                </div>
                                {room.guests && room.guests.length > 0 && (
                                  <ul className="mt-2 space-y-1 text-gray-600">
                                    {room.guests.map((g, i) => (
                                      <li key={i}>
                                        {[g.first_name, g.last_name].filter(Boolean).join(' ') || g.email || `${t('customer_panel.reservations.guest')} ${i + 1}`}
                                        {g.phone && ` · ${g.phone}`}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Yolcular */}
                      {detail.participants && detail.participants.length > 0 && (
                        <div className="border-t border-gray-200 pt-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-3">
                            {t('customer_panel.reservations.participants_title', 'Participants')}
                          </h3>
                          <ul className="space-y-2">
                            {detail.participants.map((p) => (
                              <li key={p.id} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
                                <div className="font-medium text-gray-900">
                                  {(p as { full_name?: string }).full_name || [p.first_name, p.last_name].filter(Boolean).join(' ') || `#${p.id}`}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-gray-600">
                                  {p.email && <span>{p.email}</span>}
                                  {p.phone && <span>{p.phone}</span>}
                                  {p.passport_no && <span>{t('customer_panel.reservations.passport', 'Passport')}: {p.passport_no}</span>}
                                  {p.nationality && <span>{p.nationality}</span>}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {detail.special_requests && (
                        <div className="border-t border-gray-200 pt-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('customer_panel.reservations.special_requests', 'Special requests')}</h3>
                          <p className="text-sm text-gray-600">{detail.special_requests}</p>
                        </div>
                      )}

                      {vouchers.length > 0 && (
                        <div className="border-t border-gray-200 pt-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-3">
                            {t('customer_panel.reservations.vouchers', 'Vouchers')}
                          </h3>
                          {voucherFileUrl && viewingVoucherId != null && (
                            <div className="mb-4 rounded-lg border border-gray-200 overflow-hidden bg-gray-100">
                              <div className="flex items-center justify-between px-3 py-2 bg-gray-200">
                                <span className="text-sm font-medium text-gray-700">
                                  {viewingVoucher?.voucher_no ?? `Voucher #${viewingVoucherId}`}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setViewingVoucherId(null)}
                                  className="text-gray-500 hover:text-gray-700 text-sm"
                                >
                                  {t('customer_panel.common.close', 'Close')}
                                </button>
                              </div>
                              <iframe
                                title={viewingVoucher?.voucher_no ?? 'Voucher PDF'}
                                src={voucherFileUrl}
                                className="w-full h-[360px] bg-white"
                              />
                            </div>
                          )}
                          <ul className="space-y-3">
                            {vouchers.map((v) => {
                              const fileUrl = CustomerApiService.getVoucherFileUrl(detail.id, v.id);
                              return (
                                <li
                                  key={v.id}
                                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                                >
                                  <span className="text-sm font-medium text-gray-700">
                                    {v.voucher_no ?? `Voucher #${v.id}`}
                                  </span>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setViewingVoucherId((prev) => (prev === v.id ? null : v.id))}
                                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    >
                                      {t('customer_panel.reservations.view_voucher', 'View')}
                                    </button>
                                    <a
                                      href={fileUrl}
                                      download
                                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    >
                                      {t('customer_panel.reservations.download_voucher', 'Download')}
                                    </a>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
